/**
 * リンクで確認の画面（/confirm/:id・pages/ConfirmAction.tsx）の決まり（純関数・import は launchHidden.ts だけ = これも import なし。node --test でそのまま動く）。
 * 2026-09-28 creator/confirm-link。
 *
 * 背景: 確認のフォーム（elicitation）を出せない AI エージェント（Codex 等）で「本番に公開して」と頼むと、以前の MCP は断って
 * ダッシュボードで操作の場所を探させていた（founder の試し）。いまは MCP が本体に「実行せずに登録」させ、本人にこのページのリンクを返す。
 * 本人がここで押すと、エージェントが同じ要求を 1 回だけ送り直す（apps/mcp/src/pendingActions.ts）。
 * **Kumo が代わりに実行するのではない**: 承認は「送り直しを 1 回だけ通す印」。実行はエージェントの接続（kdt_）の権限のまま。
 *
 * 画面は本体の `GET /v1/developers/pending-actions/:id`（kds_ だけ・型は Core core/pending-actions のリーダー確定版）の中身**だけ**で描く。
 * URL から読むのは id だけ（readPendingId）。WHY: URL で受けると、攻撃者が中身を偽った確認の画面を作れる（mcpAuthorize.ts と同じ理由）。
 * summary.details の文字列（件名・SQL・理由・返信）は外から来た値（エージェント・プレイヤーが書ける）= 画面は必ずテキストとして
 * 別の要素に入れる（文の中の指示に見えないように <code> 等で囲む）。client.name は DCR では自己申告 = 「名乗った名前」と書く。
 */

import { isConfigSectionHidden, isLaunchHidden } from './launchHidden.ts';
import { sqlKind } from './sqlEffect.ts';

export const CONFIRM_ACTION_PATH = '/confirm/:id';

export type PendingStatus = 'pending' | 'approved' | 'denied' | 'used' | 'expired' | 'stale' | 'revoked';
const STATUSES: readonly string[] = ['pending', 'approved', 'denied', 'used', 'expired', 'stale', 'revoked'];

/** GET /v1/developers/pending-actions/:id の応答（リーダー確定版 2026-09-28。Core の実装が入ったらそちらが正） */
export interface PendingActionView {
  id: string;
  status: string;
  createdAt?: string;
  expiresAt?: string;
  decidedAt?: string | null;
  kind?: string;
  method: string;
  path: string;
  environment?: string;
  project?: { id: string; name: string; slug: string } | null;
  /** kind: mcp = AI エージェント（MCP の接続）・cli = 端末の Kumo CLI（xkumo connect / ブラウザの xkumo login のセッション・Core cc8f6774） */
  client?: { id: string | null; name: string; kind?: 'mcp' | 'cli' } | null;
  summary?: { kind: string; details?: Record<string, unknown> } | null;
  /**
   * 待っている側（2026-10-15 core/confirm-done・本体の lib/pendingActions.ts waiterOf）。CLI の確認だけ { kind: 'cli', lastSeenAt, waiting }・
   * AI の接続（MCP）・古い本体は null / 無い。waiting: false = パソコンの CLI がもう待っていない = 押しても何も起きない（cliStoppedWaiting）
   */
  waiter?: { kind: 'cli'; lastSeenAt?: string; waiting: boolean } | null;
}
export interface PendingActionResponse {
  action: PendingActionView;
  /**
   * 束（2026-09-29 creator/confirm-bundle・Core core/confirm-polish）: 1 回の承認でまとめて通る操作の全部（登録の順）。束でなければ null / 無い。
   * 平文の id は無い（本体は id の hash しか持たない）。承認・拒否はどの id からでも束の全部に効く
   */
  bundle?: { count: number; actions: BundleActionView[] } | null;
}

/** 束の 1 行（GET …/pending-actions/:id の bundle.actions の 1 つ） */
export interface BundleActionView {
  position: number;
  status: string;
  kind: string;
  method: string;
  path: string;
  environment?: string;
  summary?: { kind: string; details?: Record<string, unknown> } | null;
}

/** 画面に出す束の 1 行: 文（describeAction）と状態 */
export interface BundleRow {
  position: number;
  status: PendingStatus;
  desc: ActionDescription;
}

/**
 * 束の行を登録の順に（position の昇順）。束でない・行が 1 つ以下なら null = 今までどおり 1 件の画面。
 * 各行の文は 1 件のときと同じ describeAction（隠す機能・節を落とす守りもそのまま効く）
 */
export function bundleRows(data: PendingActionResponse): BundleRow[] | null {
  const actions = Array.isArray(data.bundle?.actions) ? data.bundle!.actions : [];
  if (actions.length < 2) return null;
  return [...actions]
    .sort((a, b) => a.position - b.position)
    .map((a) => ({
      position: a.position,
      status: statusOf(a.status),
      desc: describeAction({ id: '', status: a.status, kind: a.kind, method: a.method, path: a.path, environment: a.environment, summary: a.summary ?? null })
    }));
}

/**
 * 承認の本文。束（2 件以上を画面に出した）なら { bundleCount: <画面に出した件数> } が要る（本体が今の件数と比べ、違えば 409 pending_bundle_changed
 * = 開いた後に足された操作を見ずに承認させない）。1 件なら本文なし（今までどおり）
 */
export function approveBody(rows: BundleRow[] | null): { bundleCount: number } | undefined {
  return rows && rows.length >= 2 ? { bundleCount: rows.length } : undefined;
}

/**
 * 決まった後の文のキー。承認の後は「許可しました。AI エージェントが 1 回だけ送れます」、束（2 件以上）は件数つきの版（approvedBundle・{count}）。
 * ほかの状態は今までどおり status ごとの 1 文。次にすること（チャットに戻る・閉じてよい）は別の 1 行（nextStepKey）。
 * 経緯: 2026-09-29 creator/prod-feel で「戻って・押したと伝えて」をやめ「自動で続けます。閉じてかまいません」にした（founder「リンクは自動がいいね」）。
 *   2026-10-13 core/confirm-flow で見直し（nextStepKey の WHY）
 */
export function statusMessageKey(status: PendingStatus, count = 1): { key: string; vars: Record<string, number> } {
  if (status === 'approved' && count >= 2) return { key: 'confirmAction.status.approvedBundle', vars: { count } };
  return { key: `confirmAction.status.${status}`, vars: {} };
}

/**
 * 承認の後に本人がすること（1 行・承認の文の下）。approved のときだけ。cli = 端末の CLI からの要求（isCliClient）。
 *   - AI の接続（MCP）: 'confirmAction.next.chat' =「1 分たっても AI が済んだと言わなければ、チャットに戻って『押した』と送って」
 *   - CLI: 'confirmAction.next.cli' =「CLI が自分で続けます。閉じてかまいません」（CLI は期限まで状態を見続ける = packages/cli/src/lib/pendingAction.ts）
 * WHY（2026-10-13 core/confirm-flow・Studio の報告 2026-10-12）: 09-29 からは「AI エージェントが自動で続けます。この画面は閉じてかまいません」だった。
 *   Claude Code・Codex は pending_action_continue を返事なしで呼び続けるので正しいが、Web の Claude はリンクを見せた所で返事を終え、
 *   続きを呼ばない = 本人がチャットで何か送るまで公開が終わらなかった。画面が「閉じてよい」と言うので、本人は待つだけだった。
 *   どのクライアントが続きを呼ぶかは画面から分からない（client.name は自己申告）= AI の接続には条件つきの 1 行を必ず出す。
 *   MCP の側で「押したら自動で進む」にする案は審査の後（docs/launch/listing-freeze-2026-10-08.md の「審査のあとに…」）
 */
export function nextStepKey(status: PendingStatus, cli: boolean): string | null {
  if (status !== 'approved') return null;
  return cli ? 'confirmAction.next.cli' : 'confirmAction.next.chat';
}

/** 押した後の画面が状態を読み直す間隔（ミリ秒）。approved の間だけ（liveStatus）。MCP の pending_action_continue の見方（3 秒おき）と同じ */
export const APPROVED_POLL_MS = 3000;

/**
 * 押した後の画面の状態（2026-10-13 core/confirm-flow）。approved の間は本体を読み直し、送り直しが済めば used（「済んだ」・公開なら公開先のリンク）、
 * 送られないまま切れれば expired に変える = 本人がチャットに戻る要るかを画面で分かる。approved でない状態は読み直さない（決まった後は変わらない）。
 * 読み直しが失敗した（fresh が null・action が無い）ときは今の状態のまま（押した直後に「期限切れ」と出さない）
 */
export function liveStatus(current: PendingStatus, fresh: PendingActionResponse | null | undefined): PendingStatus {
  if (current !== 'approved' || !fresh?.action) return current;
  const now = statusOf(fresh.action.status);
  // 束で、この id は used なのに他の行がまだ approved（送り直しの途中）= まだ済んでいない（2026-10-15 console/confirm-publishing）。
  // 今の CLI・MCP は束の最後の id を確認のページにする = 普通は来ない。来ても「公開しました」を早く出さない（confirmPublishing.ts の WHY）
  if (now === 'used' && Array.isArray(fresh.bundle?.actions) && fresh.bundle!.actions.some((a) => statusOf(a.status) === 'approved')) return 'approved';
  return now;
}

/**
 * 別のアカウントのリンクか（本体の 403 wrong_account・2026-10-08 console/confirm-wrong-account・founder）。そうなら持ち主の伏せ字のメール（例 r•••@k•••.com）。
 * WHY: 以前は本体が別の開発者にも 404 を返し、画面は「期限切れか無効」と出ていた = 別のアカウントでログインしているだけの本人が、
 *   期限切れと読んで何をすればいいか分からなかった（founder 2026-10-08）。本体が返すのは伏せ字のメールだけ（中身は返さない）。
 * 伏せ字が無い・形が違う = ''（文は伏せ字なしでも読める）。外から来た値 = 長さを切り、画面は <strong> のテキストで出す
 */
export function wrongAccountOf(err: { status?: number; code?: string; details?: Record<string, unknown> } | null | undefined): { email: string } | null {
  if (err?.code !== 'wrong_account') return null;
  return { email: text(typeof err.details?.email === 'string' ? err.details.email : '', 120) };
}

/** 本体の 409 pending_bundle_changed（開いた後に束に操作が足された）か = 画面を読み直して、もう一度見てもらう */
export function isBundleChanged(err: { code?: string } | null | undefined): boolean {
  return err?.code === 'pending_bundle_changed';
}

/** URL の :id の形（本体の id は kpa_…）。合わなければ null = 本体に問い合わせずに「無効なリンク」 */
export function readPendingId(raw: string | undefined): string | null {
  return raw && /^kpa_[A-Za-z0-9_-]{1,120}$/.test(raw) ? raw : null;
}

/** 本体の status を画面の状態に。知らない値は 'expired'（押せない側に倒す = 知らない状態でボタンを出さない） */
export function statusOf(status: unknown): PendingStatus {
  return typeof status === 'string' && STATUSES.includes(status) ? (status as PendingStatus) : 'expired';
}

/** 外から来た値を画面の文字列に（長さを切る・形の違う値は空）。配列は「, 」でつなぐ */
export function text(v: unknown, max = 300): string {
  if (typeof v === 'string') return v.trim().slice(0, max);
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.map((x) => text(x, 80)).filter(Boolean).join(', ').slice(0, max);
  return '';
}

/** 名乗った名前（自己申告・長すぎる名前は切る・空なら null = 画面は「AI エージェント」と書く） */
/** 端末の Kumo CLI からの要求か（Core cc8f6774: cli_grant のセッションの本番の書き込み）。画面の文を CLI 向けにする */
/**
 * パソコンの CLI がもう待っていないか（2026-10-15 core/confirm-done・10/15 Mega Strike）。
 * WHY: 承認は「CLI が送り直すのを 1 回だけ通す印」で、本体は代わりに実行しない。CLI が止まった後に押すと、何も起きないのに画面は
 *   「CLI が自分で続けます。閉じてかまいません」と言っていた（founder が 1 回目を押して公開されず、2 回目で公開された）。
 *   本体が「60 秒見に来ていない」を返したら、押す前に「ここで押しても何も起きない・コマンドをもう一度」と言う（ボタンを出さない）。
 * 本体が waiter を返さない（古い本体・AI の接続）= false（今までどおりの画面）
 */
export function cliStoppedWaiting(v: Pick<PendingActionView, 'waiter'> | null | undefined): boolean {
  return v?.waiter?.kind === 'cli' && v.waiter.waiting === false;
}

/** 承認を本体が「CLI がもう待っていない」で断ったか（409 pending_action_not_waiting・ページを開いた後に CLI が止まった場合） */
export const isNotWaiting = (err: { code?: string } | null | undefined) => err?.code === 'pending_action_not_waiting';

export function isCliClient(v: PendingActionView): boolean {
  return v.client?.kind === 'cli';
}

export function clientName(v: PendingActionView): string | null {
  return text(v.client?.name, 80) || null;
}

/**
 * 何をするかの文（i18n のキー `confirmAction.what.<kind の . を _ に>`・差し込む値）と、下に並べる補足（ラベルのキー・値・ブロックか）。
 * 知らない kind（本体が先に増やした等）は method と path で出す（'confirmAction.what.unknown'）= 知らないから押せない、にはしない
 * （本体がこのページに載せた = 本体が守る操作。何が起きるかは method と path で本人が判断できる）
 */
export interface ActionDescription {
  key: string;
  vars: Record<string, string>;
  extras: Array<{ key: string; value: string; block?: boolean }>;
  /** ボタンの文言のキー（`confirmAction.button.<…>`） */
  button: string;
  /**
   * 赤の注意（文より目立たせる 1 行・キーと差し込み）。今は config.push の allowTurnOff だけ（公開中のサイトを止める push・AGT-16 2026-09-29）。
   * 無ければ付けない
   */
  warning?: { key: string; vars: Record<string, string> };
}

/**
 * ボタンの名前（kind ごと）。**MCP が本人に言う名前と同じ**（apps/mcp/src/pendingActions.ts の APPROVE_LABEL・
 * 「このリンクを開いて『公開する』を押して」）。片方を直したらもう片方も（pendingAction.test.ts が en・ja の値を突き合わせる）
 */
export const BUTTON_OF: Record<string, string> = {
  'hosting.activate': 'publish',
  'hosting.finalize_activate': 'publish',
  'hosting.domain_add': 'domainAdd',
  'hosting.domain_remove': 'domainRemove',
  // Functions の独自ドメイン（2026-09-30 Compute b6bc3367 の確認の種類・Creator の依頼）。ボタンは hosting の独自ドメインと同じ名前（MCP の APPROVE_LABEL も同じ）
  'functions.domain_add': 'domainAdd',
  'functions.domain_remove': 'domainRemove',
  'config.push': 'configPush',
  'players.ban': 'ban',
  'players.unban': 'unban',
  'appeals.resolve': 'appeal',
  'functions.enable': 'fnEnable',
  'functions.disable': 'fnDisable',
  'functions.delete': 'fnDelete',
  'functions.deploy': 'fnDeploy',
  'functions.secret_put': 'secretPut',
  'functions.secret_delete': 'secretDelete',
  'functions.db_query': 'dbQuery',
  'functions.db_migrate': 'dbMigrate',
  'mail.schedule': 'mailSchedule',
  'mail.send': 'mailSend'
};

/**
 * 同じ kind でも場面で名前を変えるボタン（2026-10-04 console/plain-confirm-2・founder の表「機能を追加 → 追加する」「設定 → 変更する」…）。
 * キーは `<kind>:<buttonVariant の値>`。MCP の APPROVE_LABEL も同じキーで同じ名前を持つ（MCP は details から同じ判断をする =
 * apps/mcp/src/pendingActions.ts approveVariant。pendingLink.test.ts が describeAction と突き合わせる）
 */
export const BUTTON_VARIANT_OF: Record<string, string> = {
  'config.push:stop': 'configStop',
  'config.push:allowPublish': 'configAllowPublish',
  'config.push:turnOn': 'configTurnOn',
  'appeals.resolve:accept': 'appealAccept',
  'functions.db_query:read': 'dbQueryRead',
  'functions.db_query:delete': 'dbQueryDelete',
  'functions.db_query:insert': 'dbQueryInsert',
  'functions.db_query:schema': 'dbQuerySchema'
};

/**
 * ボタンの名前の場面（無ければ null = kind の名前）。confirmPlain.ts の見出しと同じ分け方:
 *   config.push: 公開をやめる（allowTurnOff）> 公開できる状態に（featuresOn に hosting）> 機能を追加（featuresOn）> 設定の変更
 *   appeals.resolve: 受け入れる（accept === true）/ 断る
 *   functions.db_query: SQL の種類（sqlEffect.ts）で 実行する（read）/ 消す（delete・dropColumn・drop）/ 追加する（insert）/
 *     変更する（schema）/ 書き換える（update・読めない）。見出し（confirmPlain.ts sqlLine）と同じ分け方
 * WHY 機能の名前を launchHidden で落とさないか: MCP も同じ判断をするので、両側で同じ生の値を見る（隠す機能は本体が ON にさせない）
 */
export function buttonVariant(kind: string, d: Record<string, unknown>): string | null {
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.length > 0) : []);
  if (kind === 'config.push') {
    if (list(d.allowTurnOff).length) return 'stop';
    const on = list(d.featuresOn);
    if (on.includes('hosting')) return 'allowPublish';
    return on.length ? 'turnOn' : null;
  }
  if (kind === 'appeals.resolve') return d.accept === true ? 'accept' : null;
  if (kind === 'functions.db_query') {
    const k = sqlKind(d.sql);
    if (k === 'read' || k === 'insert' || k === 'schema') return k;
    if (k === 'delete' || k === 'dropColumn' || k === 'drop') return 'delete';
    return null;
  }
  return null;
}

/**
 * テスト用（development）のときに別の文を使う「何をするか」のキー（2026-10-08 core/dev-confirm-links）。本番の文は「production の…」と言う物だけ。
 * 本体が development でも確認のリンクを受けるのは消す系・BAN・異議の回答・メール（server lib/delegationScopes.ts DEVELOPMENT_LINK_CONFIRM）。
 * BAN・異議・メールの文はもともと環境を言わない = 足さない。知らない kind（unknown）も「production で」と言うので足す
 */
const TEST_APP_WHAT: ReadonlySet<string> = new Set(['functions_delete', 'functions_purge', 'functions_delete_invalid', 'functions_domain_remove', 'unknown']);

export function describeAction(v: PendingActionView): ActionDescription {
  const kind = v.summary?.kind ?? v.kind ?? '';
  const test = v.environment === 'development';
  const d = v.summary?.details ?? {};
  const variant = buttonVariant(kind, d as Record<string, unknown>);
  const button = `confirmAction.button.${(variant && BUTTON_VARIANT_OF[`${kind}:${variant}`]) || BUTTON_OF[kind] || 'allow'}`;
  const extras: ActionDescription['extras'] = [];
  const add = (key: string, value: unknown, block = false, max = 300) => {
    const s = text(value, max);
    if (s) extras.push({ key: `confirmAction.extra.${key}`, value: s, ...(block ? { block } : {}) });
  };
  const what = (k: string, vars: Record<string, string> = {}): ActionDescription => ({ key: `confirmAction.what.${k}${test && TEST_APP_WHAT.has(k) ? '_test' : ''}`, vars, extras, button });
  // 表が意味を知らないクエリ（REV-02・本体の describeKind）。どの kind にも付きうる = 先に足す（kind ごとの補足より上）。
  // 束縛は全部のクエリに効くので、本人に見せずに承認させない。値は本体が正規化したクエリそのまま（最大 500 文字）= <code> でテキストとして出す
  add('unexpectedQuery', d.unexpectedQuery, false, 500);

  switch (kind) {
    case 'hosting.activate':
    case 'hosting.finalize_activate': {
      // いま公開中の版との差（本体 details.changes・2026-10-25 security/prompt-lava-fixes の D）。見える所は数と先頭の数件（confirmPlain.ts）、ここは本体が返した全部（20 件まで）
      const c = d.changes && typeof d.changes === 'object' ? (d.changes as Record<string, unknown>) : null;
      if (c) {
        add('addedFiles', c.addedFiles, false, 2000);
        add('changedFiles', c.changedFiles, false, 2000);
        add('removedFiles', c.removedFiles, false, 2000);
      }
      return what('hosting_activate', { version: text(d.version) || text(d.ref) || '?' });
    }
    case 'hosting.domain_add':
      return what('hosting_domain_add', { hostname: text(d.hostname, 253) });
    case 'hosting.domain_remove':
      return what('hosting_domain_remove', { hostname: text(d.hostname, 253) });
    // Functions の独自ドメイン（details = { hostname }）。以前は表に無く、既定の production_write（「実行を許可する」・method と path だけ）で説明が薄かった
    case 'functions.domain_add':
      return what('functions_domain_add', { hostname: text(d.hostname, 253) });
    case 'functions.domain_remove':
      return what('functions_domain_remove', { hostname: text(d.hostname, 253) });
    case 'config.push': {
      // 隠す機能の名前は出さない（§1c・launchHidden.ts の LAUNCH_HIDDEN_CONFIG_SECTIONS の WHY）。本体の summary も Core が直す予定 = 二重の守り
      // featuresOn / featuresOff（本体が返すようになったら）: 何を ON / OFF にするかを節の名前より先に出す。無ければ今のまま
      const on = visibleNames(d.featuresOn, isLaunchHidden);
      const off = visibleNames(d.featuresOff, isLaunchHidden);
      // ON にする機能は文そのもので先に言う（2026-09-29 creator/confirm-bundle・管理の決定）。初めての本番の公開の束は
      // 「公開（hosting）を ON にする」→「版 N を公開」と読めるように。hosting は専用の文・ほかは名前を並べる文。補足の欄に同じ名前は重ねない
      const others = on.filter((n) => n !== 'hosting');
      if (on.includes('hosting')) add('featuresOn', others);
      add('featuresOff', off);
      const sections = Array.isArray(d.changedSections) ? visibleNames(d.changedSections, isConfigSectionHidden) : [];
      add('changedSections', sections);
      // 公開中のサイトを止める push（本体の summary.details.allowTurnOff・Core core/serving-guard・AGT-16 2026-09-29）:
      //   名前の配列 = その機能を OFF にしてサイトが止まってよい（CLI の `features off hosting` が確かめた後に付ける）→ 赤の注意で先に言う。
      //   [] = 止めない / null = 読めない値（本体が 400 で断る）/ 無い = 古い本体 → 何も足さない（止まらない承認に「止まる」と言わない）
      const stop = visibleNames(d.allowTurnOff, isLaunchHidden);
      const warning = stop.length ? { key: 'confirmAction.warning.stopServing', vars: { features: stop.join(', ') } } : undefined;
      const say = (k: string, vars: Record<string, string> = {}): ActionDescription => ({ ...what(k, vars), ...(warning ? { warning } : {}) });
      if (on.includes('hosting')) return say('config_push_hosting_on');
      if (on.length) return say('config_push_on', { features: on.join(', ') });
      if (!Array.isArray(d.changedSections)) return say('config_push');
      // 落とした結果が空（隠す節だけが変わった・何も変わらない）= 利用者に見える変化は無い、と言う（隠した名前の代わりに空の行を出さない）
      return say(sections.length || off.length || stop.length ? 'config_push' : 'config_push_invisible');
    }
    case 'players.ban':
      add('reason', d.reason);
      add('until', d.until);
      return what('players_ban', { playerId: text(d.playerId, 80) });
    case 'players.unban':
      return what('players_unban', { playerId: text(d.playerId, 80) });
    case 'appeals.resolve':
      add('response', d.response, true, 1000);
      return what(d.accept === true ? 'appeals_accept' : 'appeals_reject', { appealId: text(d.appealId, 80) });
    case 'functions.enable':
      return what('functions_enable');
    case 'functions.disable':
      return what('functions_disable');
    case 'functions.delete':
      // 中身も消すか（purge）は本体が details.purge に**明示の値**で出す（2026-09-28 core/rev-auth・外部レビュー REV-02）。
      //   true = データごと削除・false = コードだけ削除・null = 指定が不正（重複・別の値。ハンドラが 400 にする = 押しても断られる）。
      // 以前は path の `?purge=true` を正規表現で読んでいた。**path は読まない**（Core と合意）: 符号化の揺れ（%70urge=true・purge=true&purge=false）で
      //   本体の意味と画面の文がずれうる = 「コードだけ」と見せてデータごと消す承認を取れてしまう。意味は本体が決めた値だけから作る。
      // boolean 以外（キーが無い = 古い本体の記録など）も不正の文に倒す = 知らないときに弱い文（データは残る）を出さない
      return what(d.purge === true ? 'functions_purge' : d.purge === false ? 'functions_delete' : 'functions_delete_invalid');
    case 'functions.deploy':
      add('mainModule', d.mainModule);
      add('moduleCount', d.moduleCount);
      add('bundleBytes', d.bundleBytes);
      add('varNames', d.varNames);
      add('crons', d.crons);
      add('message', d.message);
      // コードに書かれた外のアドレス（本体 details.outboundHosts・D）
      add('outboundHosts', d.outboundHosts, false, 2000);
      return what('functions_deploy');
    case 'functions.secret_put':
      return what('functions_secret_put', { name: text(d.name, 120) });
    case 'functions.secret_delete':
      return what('functions_secret_delete', { name: text(d.name, 120) });
    case 'functions.db_query':
      // SQL は本体が先頭 1000 文字に切って渡す。ブロック（<pre>）で全部見せる = 一部だけ見て押させない
      add('sql', d.sql, true, 1000);
      return what('functions_db_query', { binding: text(d.binding, 80) });
    case 'functions.db_migrate':
      return what('functions_db_migrate', { binding: text(d.binding, 80), count: text(d.count) || '?' });
    case 'mail.schedule':
      add('sendAt', d.sendAt);
      add('recurrence', recurrenceText(d.recurrence));
      return what(d.recurrence ? 'mail_repeat' : 'mail_schedule', { subject: text(d.subject, 200) });
    case 'mail.send':
      return what('mail_send', { subject: text(d.subject, 200) || text(d.broadcastId, 80) });
    default:
      return what('unknown', { method: text(v.method, 10).toUpperCase(), path: text(v.path, 300) });
  }
}

/** 名前の配列から、文字列でない物と隠す名前を落とす（配列でなければ空） */
function visibleNames(v: unknown, hidden: (name: string) => boolean): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0 && !hidden(x)) : [];
}

/** くり返しの予約（{ every, at }）を短い文字に。形が違えば空 */
function recurrenceText(r: unknown): string {
  if (!r || typeof r !== 'object') return '';
  const o = r as { every?: unknown; at?: unknown };
  const every = text(o.every, 10);
  const at = text(o.at, 10);
  return every ? `${every}${at ? ` @ ${at} UTC` : ''}` : '';
}

/**
 * 押した後の本体の失敗 → 画面の状態。409 pending_action_closed は details.status（もう決まっていた）、409 pending_action_stale は stale、
 * 404 は expired（無い・期限切れ・他人の = 本体は区別しない）。それ以外は null（エラーの文を出して、画面はそのまま）
 */
export function statusAfterError(err: { status?: number; code?: string; details?: Record<string, unknown> } | null | undefined): PendingStatus | null {
  if (!err || err.code === 'route_missing') return null;
  if (err.code === 'pending_action_stale') return 'stale';
  if (err.code === 'pending_action_closed') return statusOf(err.details?.status);
  if (err.status === 404 || err.status === 410) return 'expired';
  return null;
}
